import { toast } from 'react-toastify';
import Grid from 'apollo-react/components/Grid';
import TextField from 'apollo-react/components/TextField';
import Button from 'apollo-react/components/Button';
import Typography from '../../StyledTypography';
import Paper from 'apollo-react/components/Paper';
import React, { useState, useEffect } from 'react';
import './styles.css';
import './FormStyles.css';

// Import utilities and components
import {
  getUserById,
  mapIdsToUsers,
  createEmailObject,
} from './utils/userUtils';
import { validateForm, clearFieldError } from './utils/validationUtils';
import {
  searchManagers,
  searchEmployees,
  searchHeads,
  searchSingleHeads,
} from './utils/searchUtils';
import {
  createHeadChangeHandler,
  createSingleHeadChangeHandler,
  createManagerChangeHandler,
  createEmployeeChangeHandler,
} from './utils/formHandlers';

// Import form section components
import HeadSelection from './components/FormSections/HeadSelection';
import ManagerSelection from './components/FormSections/ManagerSelection';
import EmployeeSelection from './components/FormSections/EmployeeSelection';

import { useTranslation } from 'react-i18next';

/**
 * Department Form Component - Handles organizational node editing
 * @param {Object} node - Node to edit: { id: 'node1', level: 1, name: 'Engineering', description: 'Tech team', manager: [1,2], head: [3,4], employees: [5,6,7] }
 * @param {Function} editNode - Callback to save node: (node, data) => void
 * @param {Function} onCancel - Callback to cancel editing: () => void
 * @param {Array} globalSelectedManagers - Global manager tracking: [{ node_id: 'node1', manager: [1,2] }, { node_id: 'node2', manager: 5 }, ...]
 *                                          NOTE: manager is ARRAY for Level 1, SINGLE VALUE for Level 2+
 * @param {Function} setGlobalSelectedManagers - State setter for global managers
 * @param {Array} globalSelectedEmployees - Global employee tracking: [{ node_id: 'node1', employees: [3,4,5] }, { node_id: 'node2', employees: [6,7] }, ...]
 * @param {Function} setGlobalSelectedEmployees - State setter for global employees
 * @param {Array} globalSelectedHeads - Global head tracking: [{ node_id: 'node1', heads: [7,8] }, { node_id: 'node2', heads: [9] }, ...]
 * @param {Function} setGlobalSelectedHeads - State setter for global heads
 * @param {Array} users - All available users: [{ id: 1, username: 'john', email: 'john@example.com' }, { id: 2, username: 'jane' }, ...]
 * @param {Object} [data] - Optional controlled form data from parent (controlled mode)
 * @param {Function} [setData] - Optional controlled state setter from parent (controlled mode)
 * @param {Object} [errors] - Optional controlled errors from parent (controlled mode)
 * @param {Function} [setErrors] - Optional controlled state setter from parent (controlled mode)
 * @param {boolean} disableInviteUsers - Whether to disable adding users emails invitations for external users without privileges
 * @returns {JSX.Element} - Complete form with validation, autocomplete, and submission
 *
 * Logic:
 * - Can work as controlled (data/setData props) or uncontrolled (internal useState) component
 * - Manages local form state (data, selected users, filtered options)
 * - Handles different node levels (1, 2+) with different rules
 * - Provides real-time search/filtering for user selection
 * - Validates form before submission
 * - Updates global state to prevent cross-node conflicts
 *
 * Example: Editing a Level 1 node allows multi-select managers, Level 2+ allows single manager
 */

export default function DepartmentForm({
  node,
  editNode,
  onCancel,
  globalSelectedManagers,
  setGlobalSelectedManagers,
  globalSelectedEmployees,
  setGlobalSelectedEmployees,
  globalSelectedHeads,
  setGlobalSelectedHeads,
  users = [],
  isModal = false, // New prop to indicate if form is in modal mode
  isOpenedFromOrganizationForm = false,
  data: propData, // Optional controlled data from parent
  setData: propSetData, // Optional controlled setData from parent
  errors: propErrors, // Optional controlled errors from parent
  setErrors: propSetErrors, // Optional controlled setErrors from parent
  disableInviteUsers = false,
}) {
  /**
   * Form data state - holds the actual form values to be submitted
   * Structure: { name, description, manager, head, employees }
   * manager: Array for Level 1, String for Level 2+
   * head: Single value for Level 1, String/Array for Level 2
   * employees: Always Array
   *
   * Uses controlled props (propData, propSetData) if provided, otherwise uses local state
   */
  const { t } = useTranslation();
  const [localData, setLocalData] = useState({
    name: node?.name || '',
    description: node?.description || '',
    manager: node?.manager || (node?.level === 1 ? [] : ''),
    head:
      node?.head && node?.level === 1
        ? Array.isArray(node.head) && node.head.length > 0
          ? node.head[0] // take first head from array for single-select
          : Array.isArray(node.head)
            ? '' // empty array → empty string
            : node.head // already a single value
        : node?.head || (node?.level === 1 ? '' : []), // level 2+: keep as array
    invitedHead:
      node?.invitedHead && node?.level === 1
        ? Array.isArray(node.invitedHead) && node.invitedHead.length > 0
          ? node.invitedHead[0] // take first invited email for single-select
          : Array.isArray(node.invitedHead)
            ? null
            : node.invitedHead
        : node?.invitedHead || null,
    invitedManager: node?.invitedManager || (node?.level === 1 ? [] : null),
    invitedEmployees: node?.invitedEmployees || [],
    employees: node?.employees || [],
    parent_id: node?.parent_id,
  });

  // Use props if provided (controlled), otherwise use local state (uncontrolled)
  const data = propData || localData;
  const setData = propSetData || setLocalData;

  /**
   * AutoComplete filter states - hold filtered user arrays for dropdowns
   * These get updated as user types in search fields
   * Used by PrimeReact AutoComplete components
   */
  const [filteredManagers, setFilteredManagers] = useState(null);
  const [selectedManager, setSelectedManager] = useState(
    node?.manager
      ? node?.level === 1 && Array.isArray(node.manager)
        ? mapIdsToUsers(node.manager, users)
        : getUserById(node.manager, users)
      : node?.level === 1
        ? []
        : null
  );

  /**
   * UI display states - hold actual user objects for displaying in form fields
   * These are what users see in the AutoComplete components
   * Converted from IDs using utility functions
   */
  const [filteredEmployees, setFilteredEmployees] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState(
    node?.employees ? mapIdsToUsers(node.employees, users) : []
  );

  const [filteredHeads, setFilteredHeads] = useState(null);
  const [selectedHeads, setSelectedHeads] = useState(
    node?.head && Array.isArray(node.head)
      ? mapIdsToUsers(node.head, users)
      : []
  );

  const [filteredSingleHeads, setFilteredSingleHeads] = useState(null);
  const [selectedSingleHead, setSelectedSingleHead] = useState(() => {
    // level 1: single head selection (backend returns array, take first)
    if (node?.level === 1 && node?.head) {
      if (Array.isArray(node.head) && node.head.length > 0) {
        return getUserById(node.head[0], users);
      } else if (!Array.isArray(node.head)) {
        return getUserById(node.head, users);
      }
    }
    // level 1: check for invited head (backend returns array, take first)
    if (node?.level === 1 && node?.invitedHead) {
      if (Array.isArray(node.invitedHead) && node.invitedHead.length > 0) {
        return node.invitedHead[0]; // return email string directly
      } else if (
        !Array.isArray(node.invitedHead) &&
        typeof node.invitedHead === 'string'
      ) {
        return node.invitedHead;
      }
    }
    // level 2: single head inherited from parent
    if (
      node?.level === 2 &&
      node?.head &&
      Array.isArray(node.head) &&
      node.head.length === 1
    ) {
      return getUserById(node.head[0], users);
    }
    return null;
  });

  /**
   * Form validation and UI states
   * errors: Field-specific validation messages
   * showConfirmModal: Controls save confirmation dialog
   * isSubmitting: Prevents double-submission during save
   */
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localErrors, setLocalErrors] = useState({
    name: '',
    manager: '',
    head: '',
  });

  const errors = propErrors || localErrors;
  const setErrors = propSetErrors || setLocalErrors;

  /**
   * Event handlers created from utility functions
   * These handle user interactions and update both local and global states
   * Each handler is customized for the specific field type and node level
   */
  const onHeadChange = createHeadChangeHandler(
    setSelectedHeads,
    setData,
    data,
    clearFieldError,
    errors,
    setErrors,
    setGlobalSelectedHeads,
    node,
    users
  );

  const onSingleHeadChange = createSingleHeadChangeHandler(
    setSelectedSingleHead,
    setData,
    data,
    setErrors,
    errors,
    users,
    node.level === 1 ? setGlobalSelectedHeads : null, // pass for level 1 only
    node
  );

  const onManagersChange = createManagerChangeHandler(
    node,
    setSelectedManager,
    setData,
    data,
    clearFieldError,
    errors,
    setErrors,
    setGlobalSelectedManagers,
    users
  );

  const onEmployeesChange = createEmployeeChangeHandler(
    setSelectedEmployees,
    setData,
    data,
    setGlobalSelectedEmployees,
    node,
    setErrors,
    errors,
    users
  );

  /**
   * Search method creators - handle real-time filtering as user types
   * Each method debounces search by 250ms to improve performance
   * Calls corresponding search utility function with current state
   * Updates filtered arrays for AutoComplete dropdowns
   */
  const searchManagersMethod = (event) => {
    setTimeout(() => {
      const currentHead = node.level === 1 ? selectedHeads : selectedSingleHead;
      const filtered = searchManagers(
        event,
        users,
        globalSelectedManagers,
        globalSelectedEmployees,
        node,
        globalSelectedHeads,
        currentHead
      );
      setFilteredManagers(filtered);
    }, 250);
  };

  const searchEmployeesMethod = (event) => {
    setTimeout(() => {
      const currentHead = node.level === 1 ? selectedHeads : selectedSingleHead;
      const filtered = searchEmployees(
        event,
        users,
        data,
        globalSelectedEmployees,
        globalSelectedManagers,
        node,
        globalSelectedHeads || [],
        currentHead
      );
      setFilteredEmployees(filtered);
    }, 250);
  };

  const searchHeadsMethod = (event) => {
    setTimeout(() => {
      const filtered = searchHeads(
        event,
        users,
        globalSelectedHeads,
        globalSelectedManagers,
        globalSelectedEmployees,
        node,
        selectedManager
      );
      setFilteredHeads(filtered);
    }, 250);
  };

  const searchSingleHeadsMethod = (event) => {
    setTimeout(() => {
      const filtered = searchSingleHeads(event, node, users);
      setFilteredSingleHeads(filtered);
    }, 250);
  };

  /**
   * Effect hook - Updates local states when node data or users change
   * Converts node IDs back to user objects for display
   * Handles different data structures for different node levels
   * Triggers when node properties or users array changes
   */
  useEffect(() => {
    // Managers - merge existing and invited
    if (node?.manager || node?.invitedManager) {
      if (node?.level === 1) {
        const managerUsers = node.manager
          ? mapIdsToUsers(node.manager, users)
          : [];
        const invitedManagers = node.invitedManager
          ? node.invitedManager.map(createEmailObject)
          : [];
        setSelectedManager([...managerUsers, ...invitedManagers]);
      } else {
        // Single manager for level 2+
        if (node?.manager) {
          setSelectedManager(getUserById(node.manager, users));
        } else if (node?.invitedManager) {
          setSelectedManager(createEmailObject(node.invitedManager));
        } else {
          setSelectedManager(null);
        }
      }
    } else {
      setSelectedManager(node?.level === 1 ? [] : null);
    }

    // Employees - merge existing and invited
    const employeeUsers = node?.employees
      ? mapIdsToUsers(node.employees, users)
      : [];
    const invitedEmployees = node?.invitedEmployees
      ? node.invitedEmployees.map(createEmailObject)
      : [];
    setSelectedEmployees([...employeeUsers, ...invitedEmployees]);

    // Heads - handle single value for level 1, array for level 2+
    if (node.level === 1) {
      // level 1: single head value (backend may return array, extract first)
      if (node?.head) {
        const headId = Array.isArray(node.head) ? node.head[0] : node.head;
        const headUser = getUserById(headId, users);
        setSelectedSingleHead(headUser);
      } else if (node?.invitedHead) {
        // invitedHead may be array or string for level 1
        const invitedHeadEmail = Array.isArray(node.invitedHead)
          ? node.invitedHead[0]
          : node.invitedHead;
        setSelectedSingleHead(
          invitedHeadEmail ? createEmailObject(invitedHeadEmail) : null
        );
      } else {
        setSelectedSingleHead(null);
      }
    } else if ((node?.head && Array.isArray(node.head)) || node?.invitedHead) {
      // level 2+: array of heads or invited head email string (backend may return array, extract first if needed)
      const headUsers = node.head ? mapIdsToUsers(node.head, users) : [];
      const invitedHeads = node.invitedHead
        ? Array.isArray(node.invitedHead)
          ? node.invitedHead.map(createEmailObject)
          : [createEmailObject(node.invitedHead)]
        : [];
      const allHeads = [...headUsers, ...invitedHeads];

      setSelectedHeads(allHeads);

      if (node.level === 2 && allHeads.length === 1) {
        setSelectedSingleHead(allHeads[0]);
      } else if (node.level === 2 && node.head && node.head.length === 1) {
        setSelectedSingleHead(getUserById(node.head[0], users));
      } else if (node.level === 2 && node.invitedHead) {
        setSelectedSingleHead(
          Array.isArray(node.invitedHead)
            ? createEmailObject(node.invitedHead[0])
            : createEmailObject(node.invitedHead)
        );
      } else if (node.level === 2) {
        setSelectedSingleHead(null);
      }
    } else {
      setSelectedHeads([]);
      if (node.level === 2) {
        setSelectedSingleHead(null);
      }
    }
  }, [
    node?.manager,
    node?.invitedManager,
    node?.employees,
    node?.invitedEmployees,
    node?.head,
    node?.invitedHead,
    users,
  ]);

  /**
   * Effect hook - Initializes AutoComplete suggestions on component mount
   * Pre-loads filtered options for all dropdowns
   * Calls search functions with empty query to get all available options
   * Re-runs when dependencies change (users, global states, etc.)
   */
  useEffect(() => {
    const currentHead = node.level === 1 ? selectedHeads : selectedSingleHead;

    const availableManagers = searchManagers(
      { query: '' },
      users,
      globalSelectedManagers,
      globalSelectedEmployees,
      node,
      globalSelectedHeads,
      currentHead
    );
    setFilteredManagers(availableManagers);

    const availableEmployees = searchEmployees(
      { query: '' },
      users,
      data,
      globalSelectedEmployees,
      globalSelectedManagers,
      node,
      globalSelectedHeads || [],
      currentHead
    );
    setFilteredEmployees(availableEmployees);

    let availableHeads;
    if (node.level === 1) {
      availableHeads = searchHeads(
        { query: '' },
        users,
        globalSelectedHeads,
        globalSelectedManagers,
        globalSelectedEmployees,
        node,
        selectedManager
      );
      setFilteredHeads(availableHeads);
    }

    if (node.level === 2 && node?.head && Array.isArray(node.head)) {
      const availableSingleHeads = searchSingleHeads(
        { query: '' },
        node,
        users
      );
      setFilteredSingleHeads(availableSingleHeads);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    users,
    globalSelectedManagers,
    globalSelectedEmployees,
    globalSelectedHeads,
    data.manager,
    node.id,
    selectedManager,
    selectedHeads,
    selectedSingleHead,
  ]);

  /**
   * Form submission handler - Saves department data
   * @returns {Promise} - Async function that handles save process
   *
   * Logic:
   * 1. Sets loading state to prevent double-submission
   * 2. Calls editNode with current form data
   * 3. Shows success/error toast messages
   * 4. Closes modal and resets loading state
   *
   * Example: Saves { name: "Engineering", manager: [1,2], employees: [3,4,5] }
   */
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await editNode(node, data);
      setShowConfirmModal(false);
    } catch (error) {
      console.error('Error saving department:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        t('failed_to_save_department');
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: { whiteSpace: 'pre-line' },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Form validation handler - Validates form data before submission
   * @returns {boolean} - True if form is valid, false otherwise
   *
   * Logic:
   * 1. Calls validateForm utility with current data and node
   * 2. Updates errors state with validation messages
   * 3. Returns validation result to control form submission
   *
   * Example: Checks required fields, validates manager/head selections
   */
  const handleFormValidation = () => {
    const validation = validateForm(data, node, t);
    setErrors(validation.errors);
    return validation.isValid;
  };

  return (
    <div>
      <div>
        <Grid
          container
          spacing={isModal ? 1 : 3}
          sx={{ width: '100%' }}
          className={isModal ? `form-grid modal-form` : ''}
        >
          <Grid size={12}>
            <Typography variant="h4" className="form-title">
              Department Form
            </Typography>
          </Grid>

          <Grid size={12}>
            <Paper className={isModal ? `form-paper modal-form` : `form-paper`}>
              <Grid container spacing={isModal ? 1 : 3} sx={{ width: '100%' }}>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    name="name"
                    label="Department Name"
                    required
                    autoFocus
                    value={data.name}
                    error={!!errors.name}
                    helperText={errors.name}
                    onChange={(e) => {
                      setData({
                        ...data,
                        name: e.target.value,
                      });
                      clearFieldError(errors, setErrors, 'name');
                    }}
                  />
                </Grid>

                <Grid size={12}>
                  <TextField
                    fullWidth
                    name="description"
                    label="Description"
                    multiline
                    rows={2}
                    value={data.description}
                    onChange={(e) =>
                      setData({
                        ...data,
                        description: e.target.value,
                      })
                    }
                  />
                </Grid>

                <Grid size={12}>
                  <HeadSelection
                    node={node}
                    selectedHeads={selectedHeads}
                    selectedSingleHead={selectedSingleHead}
                    filteredHeads={filteredHeads}
                    filteredSingleHeads={filteredSingleHeads}
                    searchHeads={searchHeadsMethod}
                    searchSingleHeads={searchSingleHeadsMethod}
                    onHeadChange={onHeadChange}
                    onSingleHeadChange={onSingleHeadChange}
                    errors={errors}
                    users={users}
                    disableInviteUsers={disableInviteUsers}
                  />
                </Grid>

                <Grid size={12}>
                  <ManagerSelection
                    node={node}
                    selectedManager={selectedManager}
                    filteredManagers={filteredManagers}
                    searchManagers={searchManagersMethod}
                    onManagersChange={onManagersChange}
                    errors={errors}
                    disableInviteUsers={disableInviteUsers}
                  />
                </Grid>

                <Grid size={12}>
                  <EmployeeSelection
                    selectedEmployees={selectedEmployees}
                    filteredEmployees={filteredEmployees}
                    searchEmployees={searchEmployeesMethod}
                    onEmployeesChange={onEmployeesChange}
                    disableInviteUsers={disableInviteUsers}
                  />
                </Grid>

                {!isOpenedFromOrganizationForm && (
                  <Grid size={12}>
                    <div className="form-buttons">
                      <Button
                        onClick={onCancel}
                        variant="outlined"
                        color="secondary"
                        size="large"
                        className="cancel-button"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => {
                          if (handleFormValidation()) {
                            setShowConfirmModal(true);
                          }
                        }}
                        variant="contained"
                        color="primary"
                        size="large"
                        className="save-button"
                      >
                        Save Department
                      </Button>
                    </div>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <Typography variant="h5" className="modal-title">
              Confirm Save
            </Typography>

            <Typography variant="bodyDefault" className="modal-description">
              Are you sure you want to save this department? This action will
              save all changes and update the organizational structure.
            </Typography>

            <div className="modal-buttons">
              <Button
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                className="modal-submit-button"
              >
                {isSubmitting ? 'Saving...' : 'Yes, Save'}
              </Button>

              <Button
                onClick={() => setShowConfirmModal(false)}
                variant="outlined"
                color="secondary"
                disabled={isSubmitting}
                className="modal-cancel-button"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
